/**
 * Admin → Rewards Editor (v10 monthly schema).
 *
 * Edits the shared Turso `reward_calendar` table so changes reach the
 * Discord bot within 5 minutes (bot polls Turso on refresh loop). No
 * more manual JSON file uploads to PebbleHost.
 *
 * Layout:
 *   ┌─ Kind tabs (Daily | VIP) ────────────────────────────────────────┐
 *   ├─ Month tabs (Jan..Dec) ──────────────────────────────────────────┤
 *   │  Day 1 ▸ …content… (30 cards, milestone/equipment badges)       │
 *   │  Day 2 ▸ …                                                       │
 *   │  Click a day → editor modal → add/remove/reorder rewards         │
 *   └──────────────────────────────────────────────────────────────────┘
 */
import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import {
  Plus, Trash2, Save, Calendar, Crown, X, Search, ChevronUp, ChevronDown, Package, Cat, Sword, Shirt,
} from "lucide-react";

const KINDS = [
  { id: "daily", label: "Daily Rewards", icon: Calendar },
  { id: "vip",   label: "VIP Rewards",   icon: Crown },
];

const MONTHS = [
  { id: "01", label: "Jan" }, { id: "02", label: "Feb" }, { id: "03", label: "Mar" },
  { id: "04", label: "Apr" }, { id: "05", label: "May" }, { id: "06", label: "Jun" },
  { id: "07", label: "Jul" }, { id: "08", label: "Aug" }, { id: "09", label: "Sep" },
  { id: "10", label: "Oct" }, { id: "11", label: "Nov" }, { id: "12", label: "Dec" },
];

const CATALOG_KINDS = [
  { id: "item", label: "Items",     icon: Package },
  { id: "pal",  label: "Pals",      icon: Cat },
];

// Map rewards to an icon/image. For items and pals we prefer the
// image URL returned by the picker catalog; fall back to a themed
// lucide icon so unfamiliar names still render something.
function rewardIcon(r, itemByName, palByName) {
  if (r.type === "pal") {
    const src = r.image_url || palByName?.get?.(r.name)?.image;
    if (src) return <img src={src} alt="" className="w-4 h-4 rounded object-contain flex-shrink-0" loading="lazy" />;
    return <Cat size={12} className="text-purple-300" />;
  }
  const src = r.image_url || itemByName?.get?.(r.name)?.image;
  if (src) return <img src={src} alt="" className="w-4 h-4 rounded object-contain flex-shrink-0" loading="lazy" />;
  const n = (r.name || "").toLowerCase();
  if (n.includes("chillet coin") || n.includes("gold coin") || n.includes("dog coin"))
    return <span className="text-yellow-300">🪙</span>;
  if (n.includes("sphere")) return <span className="text-emerald-300">◉</span>;
  if (n.includes("armor") || n.includes("helmet")) return <Shirt size={12} className="text-orange-300" />;
  if (n.includes("shield") || n.includes("gun") || n.includes("rifle") || n.includes("sword") || n.includes("spear") || n.includes("bow") || n.includes("launcher"))
    return <Sword size={12} className="text-rose-300" />;
  return <Package size={12} className="text-[var(--muted)]" />;
}

export function RewardsEditor() {
  const toast = useToast();
  const [kind, setKind] = useState("daily");
  const [months, setMonths] = useState({});        // {"01": {"1": entry, ...}, ...}
  const [milestoneDays, setMilestoneDays] = useState([]);
  const [equipDays, setEquipDays] = useState([]);
  // Default to whatever month is "live" today so edits target rewards the
  // bot is actually serving. Backend also returns `current_month`; we use
  // that as the source of truth once the calendar loads, but we start with
  // the client's local month so the UI doesn't flash "01" first.
  const _localMonth = String(new Date().getMonth() + 1).padStart(2, "0");
  const [currentMonth, setCurrentMonth] = useState(_localMonth);
  const [activeMonth, setActiveMonth] = useState(_localMonth);
  // Tracks whether the user has manually clicked a month tab in this
  // session. When false, we keep syncing `activeMonth` to `currentMonth`
  // on every calendar reload (e.g. when swapping daily/vip tabs).
  const [monthPinned, setMonthPinned] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(false);

  // Pickers cache
  const [items, setItems] = useState([]);
  const [pals, setPals] = useState([]);
  const [templates, setTemplates] = useState([]);

  // Fast name → catalog-row lookup so day cards can look up images
  // for items/pals without scanning the full array on every render.
  const itemByName = useMemo(() => {
    const m = new Map();
    items.forEach((it) => m.set(it.name, it));
    return m;
  }, [items]);
  const palByName = useMemo(() => {
    const m = new Map();
    pals.forEach((p) => m.set(p.name, p));
    return m;
  }, [pals]);

  useEffect(() => {
    api.get("/admin/rewards/pickers/items").then(({ data }) => setItems(data.items || [])).catch(() => {});
    api.get("/admin/rewards/pickers/pals").then(({ data }) => setPals(data.pals || [])).catch(() => {});
    api.get("/admin/rewards/pickers/templates").then(({ data }) => setTemplates(data.templates || [])).catch(() => {});
  }, []);

  const loadCalendar = async (k) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/rewards/${k}`);
      setMonths(data.months || {});
      setMilestoneDays(data.milestone_days || []);
      setEquipDays(data.equipment_days || []);
      const cm = data.current_month || _localMonth;
      setCurrentMonth(cm);
      // If the user hasn't pinned a specific month tab in this session,
      // always show the currently-served month so edits land where the
      // bot will actually pull them from.
      setActiveMonth((prev) => (monthPinned && data.months?.[prev] ? prev : cm));
    } catch (e) {
      toast.error(apiError(e, "Couldn't load calendar"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadCalendar(kind); /* eslint-disable-next-line */ }, [kind]);

  // ── Day list for the active month ────────────────────────────────────
  const daysList = useMemo(() => {
    const monthDict = months[activeMonth] || {};
    return Array.from({ length: 30 }, (_, i) => {
      const dayStr = String(i + 1);
      const entry = monthDict[dayStr];
      return {
        day: dayStr,
        rewards: entry?.rewards || [],
        display_name: entry?.display_name || "",
        isEquip: equipDays.includes(i + 1),
        isMilestone: milestoneDays.includes(i + 1),
        empty: !entry,
      };
    });
  }, [months, activeMonth, milestoneDays, equipDays]);

  return (
    <div className="space-y-4 fade-up" data-testid="rewards-editor-tab">
      {/* Kind tabs */}
      <div className="glass-card p-4">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {KINDS.map((k) => (
            <button
              key={k.id}
              type="button"
              onClick={() => { setKind(k.id); setSelectedDay(null); }}
              className={`btn btn-sm ${kind === k.id ? "" : "btn-secondary"}`}
              data-testid={`rewards-kind-${k.id}`}
            >
              <k.icon size={12} /> {k.label}
            </button>
          ))}
          <span className="text-xs text-[var(--muted)] ml-auto">
            v10 · 12 months × 30 days · Bot syncs from Turso every ~60 s
          </span>
        </div>
        {/* Which month the bot is currently serving */}
        <div
          className="mb-2 px-3 py-2 rounded text-xs flex items-center gap-2 border border-yellow-300/30 bg-yellow-300/5"
          data-testid="rewards-current-month-banner"
        >
          <span className="text-yellow-300 font-semibold">Bot is serving:</span>
          <span className="font-mono">
            Month {currentMonth} ({MONTHS.find((mm) => mm.id === currentMonth)?.label || currentMonth})
          </span>
          {activeMonth !== currentMonth && (
            <span className="text-orange-300 ml-auto">
              ⚠ You are editing Month {activeMonth} — changes won&apos;t reach players until{" "}
              {MONTHS.find((mm) => mm.id === activeMonth)?.label || activeMonth}.
            </span>
          )}
        </div>
        {/* Month tabs */}
        <div className="flex gap-1 flex-wrap" data-testid="rewards-month-tabs">
          {MONTHS.map((m) => {
            const isSel = m.id === activeMonth;
            const isCurrent = m.id === currentMonth;
            return (
              <button
                key={m.id}
                onClick={() => { setActiveMonth(m.id); setMonthPinned(true); setSelectedDay(null); }}
                data-testid={`rewards-month-${m.id}`}
                className={`px-2.5 py-1 rounded text-xs font-mono transition-colors ${
                  isSel
                    ? "bg-[var(--teal)] text-black font-semibold"
                    : "bg-white/5 text-[var(--muted)] hover:bg-white/10 hover:text-[var(--bone)]"
                } ${isCurrent && !isSel ? "ring-1 ring-yellow-300/40" : ""}`}
                title={isCurrent ? "Current calendar month" : ""}
              >
                {m.label}{isCurrent ? "•" : ""}
              </button>
            );
          })}
        </div>
      </div>

      {/* Day grid */}
      <div className="glass-card p-4">
        <h4 className="label !mb-2">Month {activeMonth} — Day Grid</h4>
        {loading && <p className="text-xs text-[var(--muted)]">Loading…</p>}
        {!loading && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
            {daysList.map((d) => (
              <DayCard
                key={d.day}
                data={d}
                kind={kind}
                itemByName={itemByName}
                palByName={palByName}
                onOpen={() => setSelectedDay(d.day)}
              />
            ))}
          </div>
        )}
      </div>

      {selectedDay && (
        <DayEditorModal
          kind={kind}
          month={activeMonth}
          day={selectedDay}
          initial={months[activeMonth]?.[selectedDay]}
          items={items}
          pals={pals}
          templates={templates}
          isMilestone={milestoneDays.includes(parseInt(selectedDay, 10))}
          onClose={() => setSelectedDay(null)}
          onSaved={() => { setSelectedDay(null); loadCalendar(kind); }}
        />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Day card
// ────────────────────────────────────────────────────────────────────
function DayCard({ data, kind, itemByName, palByName, onOpen }) {
  const { day, rewards, isEquip, isMilestone, empty } = data;
  const dayNum = parseInt(day, 10);
  const badge =
    isMilestone && kind === "vip" ? { txt: "MILESTONE", cls: "border-purple-400/50 text-purple-300 bg-purple-500/10" } :
    isEquip ? { txt: "EQUIPMENT", cls: "border-orange-400/50 text-orange-300 bg-orange-500/10" } :
    null;
  return (
    <button
      onClick={onOpen}
      data-testid={`rewards-daycard-${day}`}
      className={`glass-card p-2 text-left space-y-1 hover:bg-white/5 transition-colors ${empty ? "opacity-50 border-dashed" : ""}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-mono text-[var(--muted)]">Day {dayNum}</span>
        {badge && (
          <span className={`badge text-[9px] px-1.5 py-0 border ${badge.cls}`} style={{ letterSpacing: 0.5 }}>
            {badge.txt}
          </span>
        )}
      </div>
      <div className="space-y-0.5">
        {rewards.slice(0, 4).map((r, i) => (
          <div key={i} className="flex items-center gap-1 text-[10.5px] truncate">
            {rewardIcon(r, itemByName, palByName)}
            <span className="text-[var(--bone)] truncate">
              {r.type === "pal"
                ? `Lv${r.level || 1} ${r.name}`
                : `${(r.amount || 1).toLocaleString()}x ${r.name || "?"}`}
            </span>
          </div>
        ))}
        {rewards.length > 4 && (
          <div className="text-[9px] text-[var(--muted)]">+{rewards.length - 4} more</div>
        )}
        {empty && <div className="text-[10px] text-[var(--muted)] italic">empty · click to add</div>}
      </div>
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────
// Day editor modal
// ────────────────────────────────────────────────────────────────────
function DayEditorModal({
  kind, month, day, initial, items, pals, templates, isMilestone,
  onClose, onSaved,
}) {
  const toast = useToast();
  const [rewards, setRewards] = useState(initial?.rewards ? [...initial.rewards] : []);
  const [displayName, setDisplayName] = useState(initial?.display_name || "");
  const [saving, setSaving] = useState(false);
  const [catalogKind, setCatalogKind] = useState(isMilestone && kind === "vip" ? "pal" : "item");
  const [search, setSearch] = useState("");

  const catalog = catalogKind === "item" ? items : pals;
  const filtered = useMemo(() => {
    if (!search) return catalog.slice(0, 300);
    const q = search.toLowerCase();
    return catalog.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 300);
  }, [catalog, search]);

  const move = (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= rewards.length) return;
    const next = [...rewards];
    [next[idx], next[j]] = [next[j], next[idx]];
    setRewards(next);
  };
  const remove = (idx) => setRewards(rewards.filter((_, i) => i !== idx));
  const patch = (idx, obj) => setRewards(rewards.map((r, i) => (i === idx ? { ...r, ...obj } : r)));

  const addItem = (it) => setRewards([...rewards, {
    type: "item", name: it.name, amount: 1, display_name: `1x ${it.name}`,
  }]);
  const addPal = (p) => {
    const defaultTmpl = templates[0] || "🌟 Legendary Alpha";
    setRewards([...rewards, {
      type: "pal", name: p.name, level: 50, amount: 1,
      template_key: defaultTmpl,
      display_name: `Lv50 ${p.name} (${defaultTmpl})`,
    }]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        rewards: rewards.map((r) => {
          // Rebuild display_name from live fields so admins can rely on
          // the preview matching what the bot embed will render.
          if (r.type === "pal") {
            return {
              ...r,
              display_name: `Lv${r.level || 1} ${r.name}` + (r.template_key ? ` (${r.template_key})` : ""),
            };
          }
          return { ...r, display_name: `${(r.amount || 1).toLocaleString()}x ${r.name}` };
        }),
        display_name: displayName || null,
      };
      await api.put(`/admin/rewards/${kind}/${month}/${day}`, body);
      toast.success(`Saved ${kind} · Month ${month} · Day ${day}`);
      onSaved();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const clearDay = async () => {
    if (!window.confirm(`Clear all rewards from ${kind} Month ${month} Day ${day}?`)) return;
    try {
      await api.delete(`/admin/rewards/${kind}/${month}/${day}`);
      toast.success(`Cleared Month ${month} Day ${day}`);
      onSaved();
    } catch (e) {
      toast.error(apiError(e, "Clear failed"));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="rewards-day-modal"
    >
      <div
        className="glass-card w-full max-w-5xl max-h-[85dvh] overflow-y-auto p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between flex-wrap gap-2">
          <h3 className="text-lg" style={{ fontFamily: "'Cinzel Decorative', serif" }}>
            {kind === "vip" ? "✨ VIP" : "📅 Daily"} · Month {month} · Day {day}
            {isMilestone && kind === "vip" && (
              <span className="ml-2 badge border-purple-400/40 text-purple-300 text-[10px]">MILESTONE (add a pal)</span>
            )}
          </h3>
          <div className="flex flex-wrap gap-2">
            <button onClick={save} disabled={saving} className="btn btn-sm" data-testid="rewards-modal-save">
              <Save size={12} /> {saving ? "Saving…" : "Save"}
            </button>
            <button onClick={clearDay} className="btn btn-sm btn-danger" data-testid="rewards-modal-clear">
              <Trash2 size={12} /> Clear Day
            </button>
            <button onClick={onClose} className="btn btn-sm btn-secondary" data-testid="rewards-modal-close">
              <X size={12} /> Close
            </button>
          </div>
        </div>

        {/* Current rewards list */}
        <div>
          <label className="label">Current rewards ({rewards.length})</label>
          {rewards.length === 0 ? (
            <p className="text-xs text-[var(--muted)]">No rewards yet — pick from the catalog below.</p>
          ) : (
            <ul className="space-y-2">
              {rewards.map((r, idx) => {
                const src = r.type === "pal"
                  ? (pals.find((p) => p.name === r.name)?.image)
                  : (items.find((it) => it.name === r.name)?.image);
                return (
                <li
                  key={idx}
                  className="flex items-center gap-2 bg-black/30 rounded p-2 text-xs"
                  data-testid={`rewards-modal-item-${idx}`}
                >
                  <div className="w-7 h-7 rounded bg-white/5 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {src ? (
                      <img src={src} alt="" className="w-full h-full object-contain" loading="lazy" />
                    ) : r.type === "pal" ? (
                      <Cat size={14} className="text-purple-300" />
                    ) : (
                      <Package size={14} className="text-[var(--muted)]" />
                    )}
                  </div>
                  <span className="badge text-[var(--muted)] border-white/10 uppercase">{r.type}</span>
                  <span className="text-[var(--bone)] truncate flex-1">{r.name}</span>
                  {r.type === "item" && (
                    <label className="flex items-center gap-1">
                      ×
                      <input
                        className="input inline-block w-20"
                        type="number"
                        min="1"
                        value={r.amount || 1}
                        onChange={(e) => patch(idx, { amount: parseInt(e.target.value, 10) || 1 })}
                      />
                    </label>
                  )}
                  {r.type === "pal" && (
                    <>
                      <label className="flex items-center gap-1">
                        Lv
                        <input
                          className="input inline-block w-16"
                          type="number"
                          min="1"
                          max="255"
                          value={r.level || 1}
                          onChange={(e) => patch(idx, { level: parseInt(e.target.value, 10) || 1 })}
                        />
                      </label>
                      <select
                        className="input w-auto max-w-[180px]"
                        value={r.template_key || ""}
                        onChange={(e) => patch(idx, { template_key: e.target.value || undefined })}
                      >
                        <option value="">(vanilla — no template)</option>
                        {templates.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                    </>
                  )}
                  <button onClick={() => move(idx, -1)} className="btn btn-sm btn-secondary" title="Move up">
                    <ChevronUp size={12} />
                  </button>
                  <button onClick={() => move(idx, +1)} className="btn btn-sm btn-secondary" title="Move down">
                    <ChevronDown size={12} />
                  </button>
                  <button onClick={() => remove(idx)} className="btn btn-sm btn-danger" data-testid={`rewards-modal-remove-${idx}`}>
                    <X size={12} />
                  </button>
                </li>
              );})}
            </ul>
          )}
        </div>

        {/* Catalog */}
        <div className="pt-4 border-t border-white/10 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            {CATALOG_KINDS.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => { setCatalogKind(c.id); setSearch(""); }}
                className={`btn btn-sm ${catalogKind === c.id ? "" : "btn-secondary"}`}
                data-testid={`rewards-modal-catalog-${c.id}`}
              >
                <c.icon size={12} /> {c.label}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--muted)] pointer-events-none" />
            <input
              className="input pl-8"
              placeholder={`Search ${catalogKind}s…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="rewards-modal-search"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 max-h-[40vh] overflow-y-auto">
            {filtered.map((c) => (
              <button
                key={c.name}
                onClick={() => (catalogKind === "item" ? addItem(c) : addPal(c))}
                className="glass-card p-2 text-left text-xs hover:bg-white/5 transition-colors flex items-center gap-2"
                data-testid={`rewards-modal-add-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="w-8 h-8 rounded bg-white/5 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {c.image ? (
                    <img
                      src={c.image}
                      alt=""
                      className="w-full h-full object-contain"
                      loading="lazy"
                      onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "block"; }}
                    />
                  ) : null}
                  <span style={{ display: c.image ? "none" : "block" }}>
                    {catalogKind === "pal" ? <Cat size={16} /> : <Package size={16} />}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate">{c.name}</div>
                  <div className="text-[10px] text-[var(--muted)] font-mono truncate">
                    {c.id || c.asset_id || ""}
                  </div>
                </div>
                <Plus size={12} className="flex-shrink-0" />
              </button>
            ))}
            {filtered.length === 0 && (
              <div className="col-span-full text-center text-[var(--muted)] text-sm py-6">No matches.</div>
            )}
          </div>
        </div>

        <div>
          <label className="label">Display name (embed title, optional)</label>
          <input
            className="input"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder={`e.g. Month ${month} · Day ${day} Reward`}
            data-testid="rewards-modal-display-name"
          />
        </div>
      </div>
    </div>
  );
}
